import {
  checkIsMastraErrorToolOutput,
  type MastraErrorToolOutput,
} from "@repo/app-types/agents/mastraErrorToolOutput";
import type {
  GetSectionDetailsToolInput,
  GetSectionDetailsToolOutput,
} from "@repo/app-types/entities/researchAgent";
import {
  ChatToolBase,
  ChatToolDisclosure,
  type ToolComponent,
} from "@repo/ui/chat/chatToolCall";
import { IconChartBar } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC } from "react";

export const GetSectionDetailsTool: ToolComponent<
  GetSectionDetailsToolInput,
  GetSectionDetailsToolOutput | MastraErrorToolOutput
> = (props) => {
  const sectionName = props.input.section_name;

  if (props.status.type === "running") {
    return (
      <ChatToolBase status={props.status}>
        {sectionName
          ? `Looking up the "${sectionName}" section`
          : "Looking up section details"}
      </ChatToolBase>
    );
  }

  if (checkIsMastraErrorToolOutput(props.output)) {
    return (
      <ChatToolBase status={props.status}>
        An error occurred while looking up section details
      </ChatToolBase>
    );
  }

  if (props.status.type === "complete" && props.output?.section) {
    return (
      <ChatToolDisclosure
        status={props.status}
        icon={
          <IconChartBar className="mt-0.5 size-3.5 shrink-0 text-indigo-500" />
        }
        label={sectionName ? `Reviewed "${sectionName}"` : "Section details"}
      >
        <SectionDetails result={props.output} />
      </ChatToolDisclosure>
    );
  }

  return (
    <ChatToolBase status={props.status}>
      {sectionName
        ? `Looking up the "${sectionName}" section`
        : "Looking up section details"}
    </ChatToolBase>
  );
};

const SectionDetails: FC<{
  result: GetSectionDetailsToolOutput;
}> = ({ result }) => {
  const section = result.section;

  if (!section) {
    return null;
  }

  const applicabilityDistribution =
    result.health_signals?.applicability_distribution;
  const applicabilityEntries = Object.entries(
    applicabilityDistribution?.values ?? {},
  );
  const hasApplicabilityDistribution = applicabilityEntries.some(
    ([, value]) => value.count > 0,
  );

  return (
    <div className="space-y-4 text-xs text-gray-600">
      <section className="space-y-2">
        <div className="text-[10px] font-semibold text-gray-400">Section</div>
        <div className="space-y-1">
          <div className="font-medium text-foreground">{section.name}</div>
          {section.section_path.length > 0 ? (
            <div className="text-muted-foreground">
              {section.section_path.join(" / ")}
            </div>
          ) : null}
        </div>
      </section>

      <section className="space-y-2">
        <div className="text-[10px] font-semibold text-gray-400">
          Applicability
        </div>
        {applicabilityDistribution ? (
          <>
            <div className="grid gap-2 sm:grid-cols-3">
              <Metric
                label="Interactions"
                value={applicabilityDistribution.total_interactions.toLocaleString()}
              />
              <Metric
                label="Decided"
                value={applicabilityDistribution.decided_interactions.toLocaleString()}
              />
              <Metric
                label="Date range"
                value={formatDateRange(
                  applicabilityDistribution.requested_date_range.after,
                  applicabilityDistribution.requested_date_range.before,
                )}
                compact
              />
            </div>

            {hasApplicabilityDistribution ? (
              <DistributionBars
                entries={applicabilityEntries}
                totalCount={applicabilityDistribution.total_interactions}
              />
            ) : (
              <div className="text-muted-foreground">
                No graded interactions found for this date range.
              </div>
            )}
          </>
        ) : (
          <div className="text-muted-foreground">
            Applicability stats are not available for this section.
          </div>
        )}
      </section>
    </div>
  );
};

const Metric: FC<{
  label: string;
  value: string;
  compact?: boolean;
}> = ({ label, value, compact }) => (
  <div>
    <div className="leading-5 font-semibold text-foreground">{value}</div>
    <div
      className={
        compact ? "text-[11px] text-muted-foreground" : "text-muted-foreground"
      }
    >
      {label}
    </div>
  </div>
);

const DistributionBars: FC<{
  entries: [string, { count: number; percentage: number }][];
  totalCount: number;
}> = ({ entries, totalCount }) => (
  <div className="space-y-1.5 pt-1">
    {entries.map(([label, value]) => (
      <div
        key={label}
        className="grid grid-cols-[5rem_minmax(0,1fr)_4rem] items-center gap-2"
      >
        <div className="truncate text-gray-600">{formatBucketLabel(label)}</div>
        <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-indigo-400"
            style={{ width: `${getPercent(value.count, totalCount)}%` }}
          />
        </div>
        <div className="text-right text-foreground tabular-nums">
          {value.count.toLocaleString()}
        </div>
      </div>
    ))}
  </div>
);

function formatBucketLabel(bucket: string) {
  switch (bucket) {
    case "applicable":
      return "Applicable";
    case "not_applicable":
      return "Not applicable";
    case "not_graded":
      return "Not graded";
    default:
      return bucket;
  }
}

function formatDateRange(after: string, before: string) {
  return `${formatDate(after)} to ${formatDate(before)}`;
}

function formatDate(value: string) {
  const dateTime = DateTime.fromISO(value);

  if (!dateTime.isValid) {
    return value;
  }

  return dateTime.toLocaleString(DateTime.DATE_MED);
}

function getPercent(count: number, totalCount: number) {
  if (totalCount === 0) {
    return 0;
  }

  return Math.round((count / totalCount) * 100);
}
